import { WhatsApp } from '@mui/icons-material';
import { Skeleton, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import PageContainer from '/@/components/bayer/page-container';
import useUnitProvider from '/@/core/context/unit';
import useBreadcrumbs from '/@/hooks/breadcrumbs';
import { useCropMonitoringById } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useGetUserNode, useOwnUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { openWhatsappWeb } from '/@/utils';
import AncestorsBarV2 from '/@/views/dashboard/components/ancestors-bar-v2';
import FieldviewChip from '/@/views/dashboard/components/fieldview-chip';
import CreateRecommendationModal from '/@/views/dashboard/rv/components/create-recommendation-modal';
import Header from '../../../../components/bayer/header';
import FieldMonitoringDashboardCurrentCampaign from './tabs/current-campaign';

const FieldDashboard = ({ cropMonitoringId, fieldId }: { cropMonitoringId?: number; fieldId?: number }) => {
    const units = useUnitProvider();

    const { data: cropMonitoring, isLoading: isCMLoading } = useCropMonitoringById(cropMonitoringId ?? 0);
    const fieldQuery = useFieldById(
        { id: fieldId || cropMonitoring?.field || 0 },
        { enabled: Boolean(fieldId || cropMonitoring?.field) }
    );
    const [newRecommendationModalOpened, setNewRecommendationModalOpened] = useState(false);

    const ownUserNodeQuery = useOwnUserNode();

    const growerQuery = useGetUserNode(fieldQuery.data?.userNode);

    const isRv = useIsValidatedRV();
    const hasWriteAccess = useHasWriteAccess();

    const breadcrumbs = useBreadcrumbs('fieldMonitoring.FieldMonitoringDetail');

    const locationArea = useMemo(() => {
        const area = fieldQuery.data?.area ? convertAreaFromM2(fieldQuery.data.area, units).toString() : null;
        const location = fieldQuery.data?.location;
        return [area, location].filter(Boolean).join(' - ');
    }, [fieldQuery.data?.area, fieldQuery.data?.location, units]);

    return (
        <PageContainer
            grayBackground
            header={
                <Header
                    title={fieldQuery.isValidating ? <Skeleton width={100} /> : fieldQuery.data?.name}
                    titleIcon={fieldQuery.data?.externalProvider === 'fieldview' && <FieldviewChip />}
                    buttons={[
                        ...(isRv
                            ? [
                                  {
                                      children: _('Contactar productor'),
                                      variant: 'outlined',
                                      color: 'primary',
                                      size: 'small',
                                      startIcon: <WhatsApp />,
                                      onClick: () => {
                                          openWhatsappWeb({ phone: growerQuery.data?.phone });
                                      },
                                  },
                              ]
                            : []),
                        ...(!hasWriteAccess
                            ? []
                            : [
                                  {
                                      children: _('Generar una recomendación'),
                                      variant: 'contained',
                                      color: 'secondary',
                                      size: 'medium',
                                      onClick: () => {
                                          setNewRecommendationModalOpened(true);
                                      },
                                  },
                              ]),
                    ]}
                    breadcrumbs={breadcrumbs}
                />
            }
        >
            {fieldQuery.data?.userNode && (
                <AncestorsBarV2
                    ancestorsFromNodeId={
                        fieldQuery.data?.userNode !== ownUserNodeQuery.data?.id ? fieldQuery.data.userNode : undefined
                    }
                    contactItems={locationArea ? [{ key: 'location-area', value: locationArea }] : undefined}
                />
            )}
            <Stack spacing={3} mt={4}>
                <FieldMonitoringDashboardCurrentCampaign
                    cropMonitoringId={cropMonitoringId}
                    fieldId={fieldId}
                    cropMonitoring={cropMonitoring}
                    isCMLoading={isCMLoading}
                />
            </Stack>
            <CreateRecommendationModal
                open={newRecommendationModalOpened}
                setOpen={setNewRecommendationModalOpened}
                selectedField={fieldQuery.data || undefined}
                userNodeId={fieldQuery.data?.userNode}
                entrySource='field-monitoring'
            />
        </PageContainer>
    );
};

export default FieldDashboard;
